import DOMPurify from 'isomorphic-dompurify';
import type { Script, Meta, Link } from 'unhead/types';
// Flag-gated GAM debug logging (window.__gamDebug / ?gamDebug=1). See gamDebugLog.ts.
import { gamDebugLog } from '~~/common/utils/gamDebugLog';

interface ParsedHeadEntries {
  script: Script[];
  meta: Meta[];
  link: Link[];
}

const ALLOWED_SCRIPT_HOSTNAMES = new Set([
  'securepubads.g.doubleclick.net',
  'cdn-alpha.adsinteractive.com',
]);

// External library loader scripts must be injected only once per window
// lifetime, regardless of how many Google Ad components are on the page.
// They get a key derived from the script `src` (independent of the owning
// component) so unhead dedupes them across components and across SSR→CSR
// hydration. The full src — including any version query string — is used, so
// the same URL dedupes while a different version is treated as a distinct
// library and not suppressed.
function libraryKeyForSrc (src: string | undefined): string | null {
  if (!src) {
    return null;
  }
  let hostname: string;
  try {
    hostname = new URL(src).hostname;
  } catch {
    return null;
  }
  if (!ALLOWED_SCRIPT_HOSTNAMES.has(hostname)) {
    return null;
  }
  return `gam-lib-${src}`;
}

// HTML boolean attributes that DOMPurify normalizes from valueless (`async`) to empty-string (`async=""`).
// These must map back to `true`; any other empty-string value is a genuine empty value (e.g. content="").
const BOOLEAN_ATTRS = new Set(['async', 'defer']);

function nodeAttrs (node: Element): Record<string, string | boolean> {
  const attrs: Record<string, string | boolean> = {};
  for (const attr of Array.from(node.attributes)) {
    if (attr.value === '' && BOOLEAN_ATTRS.has(attr.name)) {
      // DOMPurify-normalized boolean attribute (async="" / defer="")
      attrs[attr.name] = true;
    } else {
      // Genuine value, including explicit empty string (content="")
      attrs[attr.name] = attr.value;
    }
  }
  return attrs;
}

function sanitizeHeadCode (html: string): DocumentFragment {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'SCRIPT') {
      return;
    }
    const src = node.getAttribute('src');
    if (!src) {
      return;
    }
    try {
      if (!ALLOWED_SCRIPT_HOSTNAMES.has(new URL(src).hostname)) {
        node.removeAttribute('src');
      }
    } catch {
      node.removeAttribute('src');
    }
  });

  const fragment = DOMPurify.sanitize(html, {
    FORCE_BODY: true,
    ADD_TAGS: ['script', 'meta', 'link'],
    ADD_ATTR: ['async', 'defer', 'crossorigin', 'type', 'src', 'name', 'content', 'property', 'charset', 'rel', 'href', 'as'],
    RETURN_DOM_FRAGMENT: true,
  });

  DOMPurify.removeAllHooks();
  return fragment;
}

/**
 * Parse sanitized head code into unhead-compatible entries, each tagged with a
 * stable, deterministic `key`. The key lets unhead dedupe the same entry across
 * SSR→CSR hydration and component remounts (instead of re-injecting it).
 *
 * @param html      Raw head code from the CMS.
 * @param keyPrefix Stable per-component prefix (e.g. the component's contentId).
 *                  Library loader scripts (gpt.js) get a fixed, prefix-independent
 *                  key so they stay single across multiple components.
 */
export function parseHeadEntries (html: string, keyPrefix = 'gam'): ParsedHeadEntries {
  const fragment = sanitizeHeadCode(html);

  const script: Script[] = [];
  const meta: Meta[] = [];
  const link: Link[] = [];

  let scriptIndex = 0;
  let metaIndex = 0;
  let linkIndex = 0;

  for (const node of Array.from(fragment.querySelectorAll('script, meta, link'))) {
    const attrs = nodeAttrs(node);

    if (node.tagName === 'SCRIPT') {
      const innerContent = (node.textContent ?? '').trim();
      const src = typeof attrs.src === 'string' ? attrs.src : undefined;
      const key = libraryKeyForSrc(src) ?? `${keyPrefix}-script-${scriptIndex}`;
      script.push({ ...attrs, key, ...(innerContent ? { innerHTML: innerContent } : {}) } as Script);
      scriptIndex += 1;
    } else if (node.tagName === 'META') {
      meta.push({ ...attrs, key: `${keyPrefix}-meta-${metaIndex}` } as Meta);
      metaIndex += 1;
    } else {
      link.push({ ...attrs, key: `${keyPrefix}-link-${linkIndex}` } as Link);
      linkIndex += 1;
    }
  }

  return { script, meta, link };
}

// Session-level guard for head entry re-injection across SPA navigation.
//
// The stable `key` and `gam-lib-${src}` keys dedupe within a single unhead
// head context (SSR→CSR hydration, remount). They do NOT survive an SPA route
// change: the old page's head context is torn down and the new one is fresh, so
// a key collision can no longer be detected. This bites the DIRECT pattern,
// where the slot definition lives inline in the component's headCode
// (`<script>googletag.defineSlot('div-gpt-1')...</script>`) rather than inside a
// shared library: on B→A navigation the component would re-inject that inline
// script and call `defineSlot` again on an already-registered slot
// → "slot already defined" / empty banner slot.
//
// The guard works at the ENTRY-KEY level, not the whole-headCode level:
//   - library scripts share a `gam-lib-${src}` key → injected once per window,
//     even across components and navigations (the page-level library dedupe);
//   - per-component inline scripts get a `${keyPrefix}-script-N` key → each
//     distinct component injects once, and re-navigation to it is skipped.
// Keying on the whole headCode would be wrong when several components share an
// identical headCode (the real wrapper case): only the first would inject and
// every other component — including its own slot definition — would be dropped.
//
// This module-scoped Set persists for the lifetime of the client bundle (i.e.
// the window). On the server every request gets a fresh module instance, so
// this never suppresses the SSR head — exactly what we want.
const injectedEntryKeys = new Set<string>();

const EMPTY_HEAD_ENTRIES: ParsedHeadEntries = { script: [], meta: [], link: [] };

function entryKey (entry: { key?: string }): string | undefined {
  return typeof entry.key === 'string' ? entry.key : undefined;
}

/**
 * Parse head code into unhead entries, guarded against re-injection across SPA
 * navigation within the same window lifetime.
 *
 * Each entry whose stable key has already been injected this window is dropped;
 * the rest are recorded and returned. So a per-component inline `defineSlot`
 * script is not re-executed on B→A navigation, and a shared library script is
 * injected only once per window. On the server it always parses (the Set is
 * per-request fresh), so the SSR head is never suppressed.
 *
 * Use this instead of `parseHeadEntries` directly wherever the entries feed a
 * `useHead` call that may run again after an SPA route change.
 *
 * @param html      Raw head code from the CMS.
 * @param keyPrefix Stable per-component prefix (see `parseHeadEntries`).
 */
export function parseHeadEntriesOnce (html: string, keyPrefix = 'gam'): ParsedHeadEntries {
  if (!html) {
    return EMPTY_HEAD_ENTRIES;
  }

  const parsed = parseHeadEntries(html, keyPrefix);

  // On the server always return everything — the SSR head must be emitted, and
  // the Set is per-request fresh so there is nothing to guard against.
  if (import.meta.server) {
    return parsed;
  }

  const filterFresh = <T extends { key?: string }>(entries: T[]): T[] =>
    entries.filter((entry) => {
      const key = entryKey(entry);
      if (key === undefined) {
        // No stable key → cannot dedupe; let it through.
        return true;
      }
      if (injectedEntryKeys.has(key)) {
        gamDebugLog('head-dedupe-skip', keyPrefix, { key });
        return false;
      }
      injectedEntryKeys.add(key);
      gamDebugLog('head-dedupe-inject', keyPrefix, { key });
      return true;
    });

  return {
    script: filterFresh(parsed.script),
    meta: filterFresh(parsed.meta),
    link: filterFresh(parsed.link),
  };
}

export function sanitizeBodyCode (html: string): string {
  return DOMPurify.sanitize(html, {
    FORCE_BODY: true,
    ADD_TAGS: ['script'],
    ALLOWED_ATTR: ['id', 'style'],
  });
}
